import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtCompact } from "@/lib/format";
import { Plus, Trash2, X, Pencil, Users } from "lucide-react";
import { toast } from "sonner";

const REGIONS = {
  IN: { label: "India Payroll", currency: "INR", flag: "🇮🇳", color: "#1E3A8A" },
  AE: { label: "UAE Payroll", currency: "AED", flag: "🇦🇪", color: "#B8860B" },
};

const emptyForm = (region) => ({
  region,
  name: "",
  employee_id: "",
  role: "",
  period_month: new Date().toISOString().slice(0, 7),
  gross: "",
  hra: "0",
  allowance: "0",
  bonus: "0",
  tax: "0",
  other_deductions: "0",
  currency: REGIONS[region].currency,
  notes: "",
});

const Employees = () => {
  const [region, setRegion] = useState("IN");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);  // employee being edited
  const [form, setForm] = useState(emptyForm("IN"));

  const load = async () => {
    setLoading(true);
    try {
      const [listR, sumR] = await Promise.all([
        api.get(`/employees?region=${region}&period_month=${period}`),
        api.get(`/employees/summary?period_month=${period}`),
      ]);
      setItems(listR.data.items || []);
      setSummary(sumR.data);
    } catch (e) {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [region, period]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm(region));
    setShowForm(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      region: emp.region,
      name: emp.name || "",
      employee_id: emp.employee_id || "",
      role: emp.role || "",
      period_month: emp.period_month || period,
      gross: String(emp.gross ?? ""),
      hra: String(emp.hra ?? "0"),
      allowance: String(emp.allowance ?? "0"),
      bonus: String(emp.bonus ?? "0"),
      tax: String(emp.tax ?? "0"),
      other_deductions: String(emp.other_deductions ?? "0"),
      currency: emp.currency || REGIONS[emp.region].currency,
      notes: emp.notes || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.period_month || form.gross === "") {
      toast.error("Name, period and gross are required");
      return;
    }
    const payload = {
      ...form,
      gross: parseFloat(form.gross) || 0,
      hra: parseFloat(form.hra) || 0,
      allowance: parseFloat(form.allowance) || 0,
      bonus: parseFloat(form.bonus) || 0,
      tax: parseFloat(form.tax) || 0,
      other_deductions: parseFloat(form.other_deductions) || 0,
    };
    try {
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this employee record?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const isIN = region === "IN";
  const regionMeta = REGIONS[region];
  const regionSum = summary?.regions?.[region];

  const totals = useMemo(() => {
    const g = items.reduce((s, e) => s + Number(e.gross || 0), 0);
    const n = items.reduce((s, e) => s + Number(e.net_pay || 0), 0);
    const t = items.reduce((s, e) => s + Number(e.tax || 0), 0);
    return { g, n, t };
  }, [items]);

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
            Payroll · India + UAE
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">Employees</h1>
          <div className="text-sm font-mono text-[#666] mt-2">
            Track per-employee monthly payroll: gross · HRA · allowances · bonus · tax · net pay.
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            data-testid="period-selector"
            className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#1E3A8A] outline-none"
          />
          <button
            onClick={openAdd}
            data-testid="add-employee-button"
            className="bg-[#1E3A8A] text-white px-4 py-2 font-medium tracking-wide text-sm hover:bg-[#172a63] flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Employee
          </button>
        </div>
      </div>

      {/* Region tabs */}
      <div className="flex gap-2 border-b border-[#E5E5E5]">
        {Object.entries(REGIONS).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => setRegion(key)}
            data-testid={`tab-${key}`}
            className={`px-4 py-2 text-sm font-semibold tracking-wide border-b-2 -mb-px transition-colors ${
              region === key
                ? "border-b-[3px] text-[#0A0A0A]"
                : "border-transparent text-[#666] hover:text-[#0A0A0A]"
            }`}
            style={region === key ? { borderColor: meta.color } : {}}
          >
            <span className="mr-2">{meta.flag}</span>
            {meta.label}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard label="Headcount" value={items.length} color="#1E3A8A" testId="summary-headcount" />
        <SummaryCard
          label={`Gross Payroll (${regionMeta.currency})`}
          value={fmtMoney(totals.g, regionMeta.currency)}
          subUsd={regionSum ? fmtMoney(regionSum.gross_usd, "USD") : null}
          color="#B8860B"
          testId="summary-gross"
        />
        <SummaryCard
          label={isIN ? "Total Tax (TDS)" : "Tax Withheld"}
          value={fmtMoney(totals.t, regionMeta.currency)}
          color={isIN ? "#DC2626" : "#666"}
          testId="summary-tax"
        />
        <SummaryCard
          label={`Net Payroll (${regionMeta.currency})`}
          value={fmtMoney(totals.n, regionMeta.currency)}
          subUsd={regionSum ? fmtMoney(regionSum.net_usd, "USD") : null}
          color="#047857"
          testId="summary-net"
        />
      </section>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="employees-table">
        <table className="w-full border-collapse min-w-[1100px]">
          <thead>
            <tr className="bg-[#F5F5F5] border-b-2" style={{ borderColor: regionMeta.color }}>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Name</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Role</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Gross</th>
              {isIN && <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">HRA</th>}
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Allowance</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Bonus</th>
              {isIN && <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Tax (TDS)</th>}
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Net Pay</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">USD</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={isIN ? 10 : 8} className="text-center text-sm text-[#666] py-12">
                {loading ? "Loading..." : `No ${region === "IN" ? "India" : "UAE"} employees for ${period}. Click + Add Employee.`}
              </td></tr>
            ) : items.map((e) => (
              <tr key={e.id} className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9]" data-testid={`employee-row-${e.id}`}>
                <td className="py-3 px-4 text-sm font-medium">
                  <div>{e.name}</div>
                  {e.employee_id && <div className="text-xs font-mono text-[#999]">{e.employee_id}</div>}
                </td>
                <td className="py-3 px-4 text-sm text-[#666]">{e.role || "—"}</td>
                <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(e.gross, e.currency, 0)}</td>
                {isIN && <td className="py-3 px-4 text-sm font-mono text-right text-[#666]">{e.hra ? fmtMoney(e.hra, e.currency, 0) : "—"}</td>}
                <td className="py-3 px-4 text-sm font-mono text-right text-[#666]">{e.allowance ? fmtMoney(e.allowance, e.currency, 0) : "—"}</td>
                <td className="py-3 px-4 text-sm font-mono text-right text-[#B8860B]">{e.bonus ? fmtMoney(e.bonus, e.currency, 0) : "—"}</td>
                {isIN && <td className="py-3 px-4 text-sm font-mono text-right text-[#DC2626]">{e.tax ? fmtMoney(e.tax, e.currency, 0) : "—"}</td>}
                <td className="py-3 px-4 text-sm font-mono text-right font-semibold text-[#047857]">{fmtMoney(e.net_pay, e.currency, 0)}</td>
                <td className="py-3 px-4 text-sm font-mono text-right text-[#666]">{fmtMoney(e.net_pay_usd, "USD", 0)}</td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`} className="text-[#1E3A8A] hover:bg-[#F0F0F0] p-1.5 inline-flex" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(e.id)} data-testid={`delete-employee-${e.id}`} className="text-[#666] hover:text-[#DC2626] p-1.5 inline-flex" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <EmployeeFormModal
          isEdit={!!editing}
          region={region}
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, subUsd, color, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-[#E5E5E5] p-4"
    style={{ borderTop: `3px solid ${color}` }}
  >
    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">{label}</div>
    <div className="text-2xl font-mono font-medium tracking-tight" style={{ color }}>{value}</div>
    {subUsd && <div className="text-xs font-mono text-[#666] mt-1">≈ {subUsd}</div>}
  </div>
);

const EmployeeFormModal = ({ isEdit, region, form, setForm, onSubmit, onClose }) => {
  const isIN = region === "IN";
  const gross = parseFloat(form.gross) || 0;
  const hra = parseFloat(form.hra) || 0;
  const al = parseFloat(form.allowance) || 0;
  const b = parseFloat(form.bonus) || 0;
  const t = parseFloat(form.tax) || 0;
  const od = parseFloat(form.other_deductions) || 0;
  const previewNet = gross + hra + al + b - t - od;

  const accentColor = REGIONS[region].color;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderColor: accentColor }} onClick={(e) => e.stopPropagation()} data-testid="employee-form-modal">
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">{isEdit ? "Edit" : "Add"} Employee — {REGIONS[region].label}</h2>
            <div className="text-xs font-mono text-[#666] mt-1">Currency: {form.currency}</div>
          </div>
          <button onClick={onClose} data-testid="close-employee-form"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required testId="emp-name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#1E3A8A] outline-none" />
            </Field>
            <Field label="Employee ID (optional)">
              <input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="emp-id" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#1E3A8A] outline-none" />
            </Field>
            <Field label="Role / Designation">
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#1E3A8A] outline-none" />
            </Field>
            <Field label="Period (Month)" required>
              <input required type="month" value={form.period_month} onChange={(e) => setForm({ ...form, period_month: e.target.value })} data-testid="emp-period" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#1E3A8A] outline-none" />
            </Field>
          </div>

          <div className="border-t border-[#E5E5E5] pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">Earnings ({form.currency})</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gross / Basic" required>
                <input required type="number" step="0.01" value={form.gross} onChange={(e) => setForm({ ...form, gross: e.target.value })} data-testid="emp-gross" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#B8860B] outline-none" />
              </Field>
              {isIN && (
                <Field label="HRA (House Rent Allowance)">
                  <input type="number" step="0.01" value={form.hra} onChange={(e) => setForm({ ...form, hra: e.target.value })} data-testid="emp-hra" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#B8860B] outline-none" />
                </Field>
              )}
              <Field label={isIN ? "Special Allowance" : "Allowances"}>
                <input type="number" step="0.01" value={form.allowance} onChange={(e) => setForm({ ...form, allowance: e.target.value })} data-testid="emp-allowance" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#B8860B] outline-none" />
              </Field>
              <Field label="Bonus / Incentive">
                <input type="number" step="0.01" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} data-testid="emp-bonus" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#B8860B] outline-none" />
              </Field>
            </div>
          </div>

          <div className="border-t border-[#E5E5E5] pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">Deductions ({form.currency})</div>
            <div className="grid grid-cols-2 gap-3">
              {isIN && (
                <Field label="Tax (TDS)">
                  <input type="number" step="0.01" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} data-testid="emp-tax" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#DC2626] outline-none" />
                </Field>
              )}
              <Field label="Other Deductions">
                <input type="number" step="0.01" value={form.other_deductions} onChange={(e) => setForm({ ...form, other_deductions: e.target.value })} data-testid="emp-other-deductions" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#DC2626] outline-none" />
              </Field>
            </div>
          </div>

          <div className="border-t-2 pt-4" style={{ borderColor: "#047857" }}>
            <div className="flex justify-between items-baseline">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666]">Net Pay (preview)</div>
              <div className="text-2xl font-mono font-bold text-[#047857]" data-testid="emp-net-preview">
                {fmtMoney(previewNet, form.currency, 2)}
              </div>
            </div>
            <div className="text-xs font-mono text-[#666] mt-1">
              = Gross + HRA + Allowance + Bonus − Tax − Other Deductions
            </div>
          </div>

          <Field label="Notes (optional)">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="emp-notes" className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#1E3A8A] outline-none" />
          </Field>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#0A0A0A] px-4 py-2 text-sm font-medium hover:bg-[#F5F5F5]">Cancel</button>
            <button type="submit" data-testid="emp-submit" className="flex-1 text-white px-4 py-2 text-sm font-medium" style={{ backgroundColor: accentColor }}>
              {isEdit ? "Save Changes" : "Add Employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">
      {label}{required && <span className="text-[#DC2626] ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default Employees;
